import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '@/hooks/use.redux';
import { actionSetFCMToken } from '@/redux';
import { ServiceWorker } from '../../service-workers/service.worker.registration';
import { useUserInteraction } from '@/hooks/use.user.interaction';
import { useIsForeground } from '@/hooks/use.is.foreground';

const TAG = '[Notification-Handler]';

function BaseWebNotificationsHandler({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;
  const isForeground = useIsForeground();
  // Track if we need to request permission so the hook knows when to activate
  const [needsPermission, setNeedsPermission] = useState(() => {
    return isSupported && Notification.permission === 'default';
  });

  useEffect(() => {
    if (isForeground) {
      ServiceWorker.clearNotifications();
    }
  }, [isForeground]);

  const fetchToken = useCallback(() => {
    ServiceWorker.getNotificationToken()
      .then((token) => {
        dispatch(actionSetFCMToken(token || ''));
      })
      .catch((error) => {
        dispatch(actionSetFCMToken(''));
        console.error(TAG, 'Failed to fetch notification token', error);
      });
  }, [dispatch]);

  // Initial Check (Non-blocking)
  React.useEffect(() => {
    if (!isSupported || Notification.permission === 'denied') {
      dispatch(actionSetFCMToken(''));
      return;
    }
    if (Notification.permission === 'granted') {
      fetchToken();
    }
  }, [isSupported, dispatch, fetchToken]);

  // The Gesture-Locked Request Action
  const requestNotificationPermission = useCallback(() => {
    Notification.requestPermission()
      .then((permission) => {
        console.log(TAG, 'permissions taken after interaction', permission);
        if (permission === 'granted') {
          fetchToken();
        } else {
          dispatch(actionSetFCMToken(''));
        }
        setNeedsPermission(false);
      })
      .catch(() => {
        dispatch(actionSetFCMToken(''));
        setNeedsPermission(false);
      });
  }, [fetchToken, dispatch]);

  // Apply the utility! It will only fire `requestNotificationPermission` after a click.
  useUserInteraction(requestNotificationPermission, needsPermission);

  return children;
}

export const NotificationsHandler = React.memo(BaseWebNotificationsHandler);
